import { useMemo } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import DropCursor from '@tiptap/extension-dropcursor';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { ResizableImage } from '../extensions/ResizableImageExtension';

/**
 * useEditorExtensionsWithResize
 * 
 * TipTap extensions configuration that includes ResizableImage instead of
 * the standard Image extension. This enables drag-to-resize functionality
 * while maintaining all other editor features.
 */
export function useEditorExtensionsWithResize(placeholder: string) {
  return useMemo(() => {
    console.log('🔧 [EditorExtensionsWithResize] Creating extensions with ResizableImage');
    
    const extensions = [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            dir: 'ltr',
          },
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline cursor-pointer hover:text-blue-600',
          dir: 'ltr',
        },
      }),
      ResizableImage.configure({
        inline: false,
        allowBase64: true,
        maintainAspectRatio: true,
        minWidth: 50,
        maxWidth: 1200,
        minHeight: 50,
        maxHeight: 800,
        handleSize: 8,
        borderWidth: 2,
        HTMLAttributes: {
          class: 'rounded-md max-w-full',
        },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'table-auto',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      DropCursor.configure({
        color: '#3b82f6', // Blue-500 to match our theme
        width: 2,
        class: 'drop-cursor-line',
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
    ];
    
    console.log('✅ [EditorExtensionsWithResize] Extensions created:', {
      count: extensions.length,
      names: extensions.map(ext => ext.name),
      hasResizableImage: extensions.some(ext => ext.name === 'resizableImage'),
      hasTable: extensions.some(ext => ext.name === 'table')
    });
    
    return extensions;
  }, [placeholder]);
} 